import React from 'react';
import { Box, Flex, Text, Badge, Icon, HStack, Tooltip } from '@chakra-ui/react';
import {
  Buildings,
  FileText,
  ArrowUp,
  ArrowDown,
  XCircle,
  ArrowsClockwise,
  CurrencyDollar,
} from 'phosphor-react';
import { INfseDashboardEmpresa } from '../../hooks/useNfse';
import { formatCNPJ } from '../../utils/FormatFunctions';

interface NfseCompanyCardProps {
  empresa: INfseDashboardEmpresa;
  // eslint-disable-next-line no-unused-vars
  onClick: (empresa: INfseDashboardEmpresa) => void;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value);
}

export function NfseCompanyCard({ empresa, onClick }: NfseCompanyCardProps) {
  const totalSubstituidas = empresa.total_substituidas || 0;
  const temRetencaoFederal =
    empresa.tem_retencao_pis_cofins_tp2 ||
    empresa.tem_retencao_irrf ||
    empresa.tem_retencao_csll ||
    empresa.tem_retencao_inss;

  const temRetencaoMunicipal = empresa.tem_retencao_issqn;

  return (
    <Box
      bg="white"
      borderRadius="xl"
      border="1px"
      borderColor="gray.200"
      p={4}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        borderColor: 'blue.300',
        boxShadow: 'md',
        transform: 'translateY(-2px)',
      }}
      onClick={() => onClick(empresa)}
    >
      {/* Header: ID e Status */}
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontSize="sm" fontWeight="bold" color="gray.500">
          ID: {empresa.codi_emp}
        </Text>
        <HStack spacing={1}>
          {empresa.total_canceladas > 0 && (
            <Badge colorScheme="red" fontSize="xs" borderRadius="full">
              {empresa.total_canceladas} cancelada{empresa.total_canceladas > 1 ? 's' : ''}
            </Badge>
          )}
          {totalSubstituidas > 0 && (
            <Badge colorScheme="purple" fontSize="xs" borderRadius="full">
              {totalSubstituidas} substituída{totalSubstituidas > 1 ? 's' : ''}
            </Badge>
          )}
        </HStack>
      </Flex>

      {/* Razao Social */}
      <Flex align="center" gap={2} mb={2}>
        <Icon as={Buildings} color="blue.500" boxSize={5} />
        <Text fontSize="md" fontWeight="semibold" color="gray.800" noOfLines={2}>
          {empresa.razao_social}
        </Text>
      </Flex>

      {/* CNPJ */}
      <Text fontSize="sm" color="gray.500" mb={3} fontFamily="mono">
        {formatCNPJ(empresa.cnpj || '')}
      </Text>

      {/* Quantidades de Notas */}
      <Box bg="gray.50" borderRadius="lg" p={3} mb={3}>
        <HStack justify="space-between" mb={2}>
          <Flex align="center" gap={1}>
            <Icon as={FileText} color="blue.500" boxSize={4} />
            <Text fontSize="sm" fontWeight="medium" color="gray.700">
              {empresa.total_notas} nota{empresa.total_notas !== 1 ? 's' : ''}
            </Text>
          </Flex>
        </HStack>
        <HStack spacing={4} justify="center">
          <Tooltip label="Notas Prestadas">
            <Flex align="center" gap={1}>
              <Icon as={ArrowUp} color="green.500" boxSize={4} weight="bold" />
              <Text fontSize="sm" color="green.600" fontWeight="medium">
                {empresa.total_prestadas}
              </Text>
            </Flex>
          </Tooltip>
          <Tooltip label="Notas Tomadas">
            <Flex align="center" gap={1}>
              <Icon as={ArrowDown} color="orange.500" boxSize={4} weight="bold" />
              <Text fontSize="sm" color="orange.600" fontWeight="medium">
                {empresa.total_tomadas}
              </Text>
            </Flex>
          </Tooltip>
          {empresa.total_canceladas > 0 && (
            <Tooltip label="Notas Canceladas">
              <Flex align="center" gap={1}>
                <Icon as={XCircle} color="red.500" boxSize={4} weight="bold" />
                <Text fontSize="sm" color="red.600" fontWeight="medium">
                  {empresa.total_canceladas}
                </Text>
              </Flex>
            </Tooltip>
          )}
          {totalSubstituidas > 0 && (
            <Tooltip label="Notas Substituídas">
              <Flex align="center" gap={1}>
                <Icon as={ArrowsClockwise} color="purple.500" boxSize={4} weight="bold" />
                <Text fontSize="sm" color="purple.600" fontWeight="medium">
                  {totalSubstituidas}
                </Text>
              </Flex>
            </Tooltip>
          )}
        </HStack>
      </Box>

      {/* Valor Total */}
      <Flex align="center" gap={2} mb={3} justify="center">
        <Icon as={CurrencyDollar} color="green.600" boxSize={5} />
        <Text fontSize="lg" fontWeight="bold" color="green.600">
          {formatCurrency(empresa.valor_total)}
        </Text>
      </Flex>

      {/* Badges de Retencoes */}
      <Flex wrap="wrap" gap={2} justify="center">
        {empresa.tem_retencao_issqn && (
          <Tooltip label={`ISSQN retido: ${formatCurrency(empresa.valor_issqn)}`}>
            <Badge colorScheme="red" fontSize="xs" px={2} py={1} borderRadius="md">
              ISSQN
            </Badge>
          </Tooltip>
        )}
        {empresa.tem_retencao_pis_cofins_tp2 && (
          /* Não exibir v_pis/v_cofins aqui: são débito de apuração própria
             (NT-007), não o valor retido — que vai somado dentro da CSLL */
          <Tooltip
            label={
              empresa.notas_ret_pis_cofins != null
                ? `${empresa.notas_ret_pis_cofins} nota(s) com retenção de PIS/COFINS (valor retido somado na CSLL)`
                : 'Com retenção de PIS/COFINS (valor retido somado na CSLL)'
            }
          >
            <Badge colorScheme="blue" fontSize="xs" px={2} py={1} borderRadius="md">
              PIS/COFINS
            </Badge>
          </Tooltip>
        )}
        {empresa.tem_retencao_irrf && (
          <Tooltip label={`IRRF: ${formatCurrency(empresa.valor_irrf)}`}>
            <Badge colorScheme="yellow" fontSize="xs" px={2} py={1} borderRadius="md">
              IRRF
            </Badge>
          </Tooltip>
        )}
        {empresa.tem_retencao_csll && (
          <Tooltip label={`CSLL: ${formatCurrency(empresa.valor_csll)}`}>
            <Badge colorScheme="teal" fontSize="xs" px={2} py={1} borderRadius="md">
              CSLL
            </Badge>
          </Tooltip>
        )}
        {empresa.tem_retencao_inss && (
          <Tooltip label={`INSS: ${formatCurrency(empresa.valor_inss)}`}>
            <Badge colorScheme="purple" fontSize="xs" px={2} py={1} borderRadius="md">
              INSS
            </Badge>
          </Tooltip>
        )}
        {!temRetencaoFederal && !temRetencaoMunicipal && (
          <Badge colorScheme="gray" fontSize="xs" px={2} py={1} borderRadius="md">
            Sem retenções
          </Badge>
        )}
      </Flex>
    </Box>
  );
}
